import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PenLine, ClipboardList, Stethoscope, User, Calendar } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const TURNO_LBL = { mattina: "Mattina", pomeriggio: "Pomeriggio", notte: "Notte", sera: "Sera/Notte", cucina: "Cucina" };

export default function RegistroFirme() {
  const { selectedStructure, structures } = useAuth();
  const [operatore, setOperatore] = useState("__all__");
  const [tipo, setTipo] = useState("__all__");
  const [da, setDa] = useState("");
  const [a, setA] = useState("");

  const { data: operatoriList = [] } = useQuery({
    queryKey: ["firma-operatori", selectedStructure],
    queryFn: async () => (await api.get("/firma/operatori", { params: selectedStructure && selectedStructure !== "all" ? { structure_id: selectedStructure } : {} })).data,
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["registro-firme", selectedStructure, operatore, tipo, da, a],
    queryFn: async () => {
      const params = {};
      if (selectedStructure && selectedStructure !== "all") params.structure_id = selectedStructure;
      if (operatore !== "__all__") params.operatore = operatore;
      if (tipo !== "__all__") params.tipo = tipo;
      if (da) params.da = da;
      if (a) params.a = a;
      return (await api.get("/firma/registro", { params })).data;
    },
  });

  const structName = (sid) => structures.find((s) => s.id === sid)?.nome || "—";
  const resetFilters = () => { setOperatore("__all__"); setTipo("__all__"); setDa(""); setA(""); };

  return (
    <div className="space-y-5" data-testid="registro-firme-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold flex items-center gap-2"><PenLine className="text-primary" /> Registro firme</h1>
        <p className="text-sm text-muted-foreground mt-1">Chi ha firmato cosa, con data e ora esatte. Consegne ed eventi sanitari, filtrabili per operatore e periodo.</p>
      </div>

      <div className="gr-card p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="registro-filtri">
        <div>
          <Label className="text-xs font-semibold mb-1 block">Operatore (firma)</Label>
          <Select value={operatore} onValueChange={setOperatore}>
            <SelectTrigger data-testid="registro-filtro-operatore"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti gli operatori</SelectItem>
              {operatoriList.map((o) => { const n = `${o.cognome} ${o.nome}`.trim(); return <SelectItem key={o.id} value={n}>{n}</SelectItem>; })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1 block">Tipo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="registro-filtro-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutto</SelectItem>
              <SelectItem value="consegna">Solo consegne</SelectItem>
              <SelectItem value="evento">Solo eventi sanitari</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1 block">Dal</Label>
          <Input type="date" value={da} onChange={(e) => setDa(e.target.value)} data-testid="registro-filtro-da" />
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1 block">Al</Label>
          <Input type="date" value={a} onChange={(e) => setA(e.target.value)} data-testid="registro-filtro-a" />
        </div>
        <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">{rows.length} firme trovate</span>
          <Button variant="outline" size="sm" onClick={resetFilters} data-testid="registro-reset">Azzera filtri</Button>
        </div>
      </div>

      <div className="gr-card p-0 overflow-hidden" data-testid="registro-lista">
        {isLoading ? (
          <div className="p-6 text-muted-foreground">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-muted-foreground italic">Nessuna firma per i filtri selezionati.</div>
        ) : (
          <div className="divide-y">
            {rows.map((r) => (
              <div key={`${r.tipo}-${r.id}`} className="p-3 sm:p-4 flex items-start gap-3" data-testid={`registro-row-${r.id}`}>
                <span className={`shrink-0 mt-0.5 inline-grid place-items-center w-9 h-9 rounded-full ${r.tipo === "evento" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"}`}>
                  {r.tipo === "evento" ? <Stethoscope size={18} /> : <ClipboardList size={18} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.tipo === "evento" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{r.tipo === "evento" ? "Evento sanitario" : "Consegna"}</span>
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-slate-800" data-testid={`registro-firma-${r.id}`}><User size={13} /> {r.operatore}</span>
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Calendar size={12} /> {fmtDateTime(r.data_ora)}</span>
                  </div>
                  <div className="text-[13px] text-slate-700 mt-1">{r.descrizione || "—"}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {r.ospite && <span>Ospite: <b className="text-slate-600">{r.ospite}</b></span>}
                    {r.turno && <span>· Turno {TURNO_LBL[r.turno] || r.turno}</span>}
                    {r.responsabile && <span>· Responsabile: <b className="text-slate-600">{r.responsabile}</b></span>}
                    {(selectedStructure === "all" || !selectedStructure) && <span>· {structName(r.structure_id)}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
